import { useAnimationControls } from "framer-motion";
import { DateTime } from "luxon";
import type React from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useRemovalReasonState } from "@/components/approvalDialogs/hooks/useRemovalReasonState";
import { usePayrollConfirmation } from "@/components/hooks/usePayrollConfirmation";
import { useDeleteRecurringShift } from "@/components/RecurringShift/util/hooks";
import { isPunchlessRow } from "@/components/StaffTable/utils";
import { useAnalytics } from "@/contexts/analytics/hooks";
import { useChosenDepartment } from "@/contexts/ChosenDepartment";
import { useDraftMode } from "@/contexts/DraftModeContext";
import { FEATURE_FLAGS } from "@/contexts/FeatureFlagsProvider";
import { useSelfScheduling } from "@/contexts/SelfScheduling/SelfSchedulingContext";
import { useSnackbar } from "@/contexts/SnackbarContext";
import { useCallOffReplacementGate } from "@/hooks/useCallOffReplacementGate";
import { useCallOffShift } from "@/hooks/useCallOffShift";
import { useDepartmentFlags } from "@/hooks/useDepartmentFlags";
import { useErrorSnackbar } from "@/hooks/useErrorSnackbar";
import { useHandleNurseChange } from "@/hooks/useHandleNurseChange";
import { useFeatureFlag } from "@/hooks/useOpenFeatureFlag";
import { useRosterLookup } from "@/hooks/useRosterLookup";
import { useScheduleDayOf } from "@/hooks/useScheduleDayOf";
import { useScheduleSaves } from "@/hooks/useScheduleSaves";
import { useShiftDayWeeks } from "@/hooks/useScheduleWeeks";
import { useShouldWarnRemovingUnionStaff } from "@/hooks/useShouldWarnRemovingUnionStaff";
import { isCheckboxHintOpen } from "@/utils/callOffReplacementAdvice";
import { nurseShiftsInTimeFrame } from "@/utils/departmentWeeks";
import { AbsenceType, type Nurse, type NurseInShift, type WeekData } from "@/utils/models";
import { isAgencyNurse } from "@/utils/warningDialogs/WarningDialog";
import { offersReplacementSearch } from "./offersReplacementSearch";
import { DeleteRecurringOrSingleMode, type DeleteRecurringOrSingleShiftProps } from "./types";

type RemovalMirrors = { shiftsToClear: NurseInShift[]; serverCascades: boolean; isAgency: boolean };

/**
 * The rows on the OTHER side of this pair that the server takes with this one, so local state can
 * drop them too. Empty ⇒ nothing cascades and only the row itself is removed.
 *
 * Mirrors `resolve_agency_mirror_deletions` (backend/database/agency_mirror.py) rather than
 * re-deciding: it cascades for an agency nurse, OR when the ACTUAL side of an exact-time pair
 * records no punch at all. Only the actual table carries the punch flags, so which side to ask is
 * `showActual` — asking the source row regardless would misread a `missingFromScheduled` row,
 * whose flags this FE synthesizes onto a scheduled shift for display.
 *
 * Two different questions, deliberately not collapsed into one:
 *
 * `shiftsToClear` is what LOCAL STATE drops, and agency keeps its original whole-day sweep
 * (INH-3359) — broader than the server on purpose, and unchanged from before APP-702.
 *
 * `serverCascades` is what the SERVER will really delete, and so pairs on exact start/end for
 * every branch, exactly as `resolve_agency_mirror_deletions` does. It gates the payroll warning,
 * which must not fire for a cascade that will not happen.
 */
const removalMirrorsOf = (weeks: WeekData[], nurse: Nurse | undefined, nurseInShift: NurseInShift, showActual: boolean): RemovalMirrors => {
	const isAgency = !!nurse?.agencyName || (!!nurse?.employmentType && isAgencyNurse(nurse.employmentType));
	// Like the provider's lookup, a nurse who isn't on the roster has no mirrors to clear.
	const sameDayMirrors = nurse
		? nurseShiftsInTimeFrame(weeks, nurseInShift.nurseId, nurseInShift.startTime.startOf("day"), nurseInShift.endTime.endOf("day"), !showActual)
		: [];
	// Only the actual table carries the punch flags, so `showActual` says which side to ask.
	const exactTimePairs = sameDayMirrors.filter(
		(mirror) => mirror.startTime.toMillis() === nurseInShift.startTime.toMillis() && mirror.endTime.toMillis() === nurseInShift.endTime.toMillis(),
	);
	return {
		isAgency,
		shiftsToClear: isAgency ? sameDayMirrors : exactTimePairs.filter((mirror) => isPunchlessRow(showActual ? nurseInShift : mirror)),
		serverCascades: exactTimePairs.some((mirror) => isAgency || isPunchlessRow(showActual ? nurseInShift : mirror)),
	};
};

export const useDeleteRecurringOrSingleState = ({
	nurseInShift,
	onFinished,
	source,
	showActual,
	applyImmediately,
	// DEFAULT: true preserves every pre-existing caller (the scheduled-view menus), which never pass
	// this and must keep offering the biweekly/"all recurring" modes. Only the actual view opts out.
	allowRecurringModes = true,
}: DeleteRecurringOrSingleShiftProps) => {
	const isCallOffEnabled = useFeatureFlag(FEATURE_FLAGS.CallOffAgent);
	const { isDataInSelfSchedulingPeriod } = useSelfScheduling();
	const { isDraftMode } = useDraftMode();
	const { track, trackError } = useAnalytics();
	const { showSnackbar } = useSnackbar();
	const { t } = useTranslation();
	const {
		removeNurse,
		isLoading: { removeNurse: removeNurseLoading },
	} = useHandleNurseChange();
	const { selectedReason, handleReasonChange, removalMessage, setRemovalMessage, isReasonValid, getFullReason, dontRenderReasonSelector } =
		useRemovalReasonState({ hospitalRecordsCallIn: !applyImmediately });
	const { chosenDepartment } = useChosenDepartment();
	const { isHospitalDemo } = useDepartmentFlags();
	const showErrorSnackbar = useErrorSnackbar();
	const { getNurseById } = useRosterLookup(chosenDepartment.department_id);
	const { removeNurseFromSpecificDate } = useScheduleSaves();
	const scheduleDayOf = useScheduleDayOf();
	// The shift's day, for its schedule/actual mirrors: what the removal clears and whether payroll is touched.
	const shiftDay = useShiftDayWeeks(scheduleDayOf(nurseInShift).date);
	const [chosenMode, setChosenMode] = useState<DeleteRecurringOrSingleMode>(DeleteRecurringOrSingleMode.DeleteSingle);
	// Pinned rather than merely un-rendered: a caller that forbids series deletion must not be able to
	// reach `deleteRecurringShift` at all, whatever a stale radio value or a future caller does.
	const selectedMode = allowRecurringModes ? chosenMode : DeleteRecurringOrSingleMode.DeleteSingle;
	const [dateToDeleteFrom, setDateToDeleteFrom] = useState<DateTime>(nurseInShift.startTime);
	const [didConfirmPayrollWarning, setDidConfirmPayrollWarning] = useState<boolean>(false);
	const [deleteUpdateDateSelectionValid, setDeleteUpdateDateSelectionValid] = useState<boolean>(false);
	const { mutateAsync: deleteRecurringShift, isPending: deleteRecurringShiftLoading } = useDeleteRecurringShift();
	const isSelfSchedulingShift = isDataInSelfSchedulingPeriod(nurseInShift.startTime);
	const { mutateAsync: callOffShift, isPending: isCallOffPending } = useCallOffShift(undefined);
	const shouldWarnRemovingUnion = useShouldWarnRemovingUnionStaff({
		isRecurringShift: !!nurseInShift.recurringShiftId,
		nurseId: nurseInShift.nurseId,
	});
	// The manager's explicit tick, or null while they haven't touched the box. See `shouldFindReplacementForShift`.
	const [replacementChoice, setShouldFindReplacementForShift] = useState<boolean | null>(null);
	const [showReasonError, setShowReasonError] = useState(false);
	const reasonShakeControls = useAnimationControls();
	const [callInTime, setCallInTime] = useState<DateTime | null>(() => (applyImmediately ? null : DateTime.now()));
	const [callInDate, setCallInDate] = useState<DateTime>(() => (applyImmediately ? nurseInShift.startTime : DateTime.now()));

	// The day as shown, for the payroll warning before Confirm; Confirm reads the day again in case it arrived since.
	const shownMirrors = useMemo(
		() => removalMirrorsOf(shiftDay.weeks, getNurseById(nurseInShift.nurseId), nurseInShift, showActual),
		[getNurseById, shiftDay.weeks, nurseInShift, showActual],
	);

	// Payroll-impacting either because this IS the actual row, or because the server cascade deletes one.
	const needsPayrollConfirmation = usePayrollConfirmation(showActual || shownMirrors.serverCascades);
	// Whether a payroll-impacting removal asks for the tick in this department, for the day Confirm reads.
	const payrollChangesNeedConfirmation = usePayrollConfirmation(true);

	const renderFindReplacementForShift = useMemo(
		() =>
			offersReplacementSearch({
				start: nurseInShift.startTime,
				department: chosenDepartment,
				isCallOffEnabled,
				isDraftMode,
				inSelfSchedulingPeriod: isSelfSchedulingShift,
				showActual,
				mode: selectedMode,
			}),
		[isCallOffEnabled, isDraftMode, isSelfSchedulingShift, nurseInShift.startTime, chosenDepartment, selectedMode, showActual],
	);

	// Mode-aware replacement advice from the backend gate (was FE HPPD-only math), worded by what it rests on.
	// Null until the gate is ready.
	const {
		advice: replacementAdvice,
		isGateReady,
		isGateError,
		gateError,
	} = useCallOffReplacementGate({
		departmentId: nurseInShift.departmentId,
		shiftId: nurseInShift.id,
		enabled: renderFindReplacementForShift,
	});

	const deleteValid = useMemo(() => {
		if (selectedMode === DeleteRecurringOrSingleMode.DeleteRecurring || selectedMode === DeleteRecurringOrSingleMode.DeleteBiweeklyDay) {
			return deleteUpdateDateSelectionValid && (!needsPayrollConfirmation || didConfirmPayrollWarning);
		}
		if (selectedMode === DeleteRecurringOrSingleMode.DeleteSingle) {
			return (!applyImmediately || selectedReason !== AbsenceType.CALL_IN || callInTime !== null) && (!needsPayrollConfirmation || didConfirmPayrollWarning);
		}
	}, [deleteUpdateDateSelectionValid, selectedMode, needsPayrollConfirmation, didConfirmPayrollWarning, applyImmediately, selectedReason, callInTime]);

	// Derived, not written by an effect: an effect applies the default one render AFTER the advice arrives,
	// and that render shows "unticked against the recommendation" — a flash of the wrong warning.
	// Gated on readiness so the box never shows ticked while Confirm would send a plain removal (a failed
	// background refetch); the manager's choice is kept and comes back once the gate recovers.
	const shouldFindReplacementForShift = renderFindReplacementForShift && isGateReady && (replacementChoice ?? replacementAdvice?.defaultTick === true);

	useEffect(() => {
		if (isGateError) {
			showErrorSnackbar(t("error.general"), { shiftId: nurseInShift.id }, gateError);
		}
	}, [gateError, isGateError, nurseInShift.id, showErrorSnackbar, t]);

	useEffect(() => {
		if (selectedReason) setShowReasonError(false);
	}, [selectedReason]);

	const selectedModeChangeHandler = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
		const newMode = event.target.value as DeleteRecurringOrSingleMode;
		setChosenMode(newMode);
	}, []);

	const clearMirrorShiftsFromLocalState = useCallback(
		(date: DateTime, shiftsToClear: NurseInShift[]): void => {
			for (const foundShift of shiftsToClear) {
				removeNurseFromSpecificDate(
					date,
					foundShift.shiftType,
					foundShift.nurseId,
					!showActual,
					foundShift.id,
					isDraftMode || isSelfSchedulingShift,
					undefined,
					true,
				);
			}
		},
		[showActual, removeNurseFromSpecificDate, isDraftMode, isSelfSchedulingShift],
	);

	const handleDeleteSingle = useCallback(
		async (mirrors: RemovalMirrors): Promise<void> => {
			const { date, shiftType } = scheduleDayOf(nurseInShift);
			const selectedReasonString = getFullReason();
			const sendToServer = !isDraftMode;
			const sendCallOffShift = isGateReady && shouldFindReplacementForShift && sendToServer && !isHospitalDemo;
			track("remove_nurse_single_click", { nurse_id: nurseInShift?.nurseId, shift_id: nurseInShift?.id });
			try {
				const callInTimeForRequest = selectedReason === AbsenceType.CALL_IN ? (callInTime ?? undefined) : undefined;
				if (sendToServer) {
					if (sendCallOffShift) {
						await callOffShift({
							departmentId: nurseInShift.departmentId,
							shiftId: nurseInShift.id,
							nurseId: nurseInShift.nurseId,
							reason: selectedReasonString,
							removalMessage: removalMessage,
							changeSource: source,
						});
					} else {
						await removeNurse({
							nurse: nurseInShift,
							date,
							shiftType,
							message: !applyImmediately && isSelfSchedulingShift ? "Self Schedule Balancing" : selectedReasonString,
							removalMessage: removalMessage || undefined,
							callInTime: callInTimeForRequest,
							applyImmediately,
							isActual: showActual,
							source,
						});
					}
				}
				// Gated on "the server cascades this pair", not on agency: leaving a pending strikethrough on
				// a row the server has actually deleted re-surfaces the person the scheduler just removed,
				// and a delete-only publish answers `shouldRefresh: false` so nothing refetches to correct it.
				if (mirrors.shiftsToClear.length > 0) {
					clearMirrorShiftsFromLocalState(date, mirrors.shiftsToClear);
				}
				removeNurseFromSpecificDate(
					date,
					shiftType,
					nurseInShift.nurseId,
					showActual,
					nurseInShift.id,
					!applyImmediately && (isDraftMode || isSelfSchedulingShift),
					!applyImmediately && (isDraftMode || isSelfSchedulingShift) ? selectedReasonString : undefined,
					// `forceRemove`. Agency keeps its unconditional pre-APP-702 behaviour (INH-3359) — narrowing
					// it to "a mirror exists" would leave a pending strikethrough in the 300+ departments that
					// promote nothing, which is the same ghost-row bug this change exists to remove. Past corrections
					// are published immediately, so they never leave a pending-delete row either.
					applyImmediately || mirrors.isAgency || mirrors.serverCascades,
				);
				showSnackbar(
					t("nurse.removed.success", { nurseName: nurseInShift.name, day: nurseInShift.startTime.toFormat("ccc"), shiftType: t(nurseInShift.shiftType) }),
					"success",
				);

				track("remove_nurse_single_success", {
					shift_id: nurseInShift.id,
					nurse_id: nurseInShift.nurseId,
					shift_type: nurseInShift.shiftType,
					date: nurseInShift.startTime.toFormat("yyyy-MM-dd HH:mm"),
					reason: nurseInShift.message,
					notes: nurseInShift.notes,
				});
			} catch (e) {
				showErrorSnackbar("Failed to remove staff member", { nurseId: nurseInShift?.id }, e);
				onFinished(false, e);
				return;
			}

			// if we reached this point - the removal was successful, even if call off failed
			onFinished(true);
		},
		[
			showSnackbar,
			showErrorSnackbar,
			showActual,
			removeNurseFromSpecificDate,
			removeNurse,
			nurseInShift,
			t,
			isDraftMode,
			track,
			scheduleDayOf,
			getFullReason,
			removalMessage,
			callInTime,
			applyImmediately,
			selectedReason,
			isSelfSchedulingShift,
			clearMirrorShiftsFromLocalState,
			onFinished,
			shouldFindReplacementForShift,
			isGateReady,
			callOffShift,
			isHospitalDemo,
			source,
		],
	);

	const handleConfirm = useCallback(async () => {
		if (
			selectedMode === DeleteRecurringOrSingleMode.DeleteSingle &&
			!dontRenderReasonSelector &&
			(!isSelfSchedulingShift || applyImmediately) &&
			!isReasonValid
		) {
			setShowReasonError(true);
			await reasonShakeControls.start({
				x: [0, -4, 4, -4, 4, 0],
				transition: { duration: 0.4, ease: "easeInOut", times: [0, 0.2, 0.4, 0.6, 0.8, 1] },
			});
			return;
		}
		// The shift's day says which mirrors the removal clears and whether payroll is touched, so it is read once
		// clicked: Confirm is never held back while the day loads.
		let mirrors: RemovalMirrors;
		try {
			mirrors = removalMirrorsOf(await shiftDay.loadWeeks(), getNurseById(nurseInShift.nurseId), nurseInShift, showActual);
		} catch (e) {
			showErrorSnackbar(t("staffing.changes.schedule.load.failed"), { nurse_id: nurseInShift.nurseId, shift_id: nurseInShift.id }, e);
			return;
		}
		// A day that arrived only now can turn out to touch payroll: the warning is shown and waits for its tick.
		if (payrollChangesNeedConfirmation && (showActual || mirrors.serverCascades) && !didConfirmPayrollWarning) return;
		if (selectedMode === DeleteRecurringOrSingleMode.DeleteSingle) {
			await handleDeleteSingle(mirrors);
		} else if (nurseInShift.recurringShiftId) {
			const isBiweekly = selectedMode === DeleteRecurringOrSingleMode.DeleteBiweeklyDay;
			const startDateStr = dateToDeleteFrom.toFormat("yyyy-MM-dd");
			const biweeklyWeekday = nurseInShift.startTime.toFormat("cccc").toLowerCase();
			const biweeklyAnalyticsProps = {
				shift_id: nurseInShift.id,
				nurse_id: nurseInShift.nurseId,
				recurring_shift_id: nurseInShift.recurringShiftId,
				weekday: biweeklyWeekday,
				start_date: startDateStr,
			};
			if (isBiweekly) {
				track("delete_biweekly_weekday_click", {
					shift_id: nurseInShift.id,
					nurse_id: nurseInShift.nurseId,
					recurring_shift_id: nurseInShift.recurringShiftId,
				});
				track("delete_biweekly_weekday_start", biweeklyAnalyticsProps);
			}
			try {
				await deleteRecurringShift({
					id: nurseInShift.recurringShiftId,
					nurse_id: nurseInShift.nurseId,
					department_id: chosenDepartment.department_id,
					source: source,
					start_date: startDateStr,
					...(isBiweekly && {
						mode: "biweekly_day" as const,
						// Backend expects lowercase Python `calendar.day_name`, e.g. "monday".
						weekday: biweeklyWeekday,
					}),
				});
				if (isBiweekly) {
					track("delete_biweekly_weekday_success", biweeklyAnalyticsProps);
				}
				onFinished(true);
			} catch (e) {
				if (isBiweekly) {
					trackError("delete_biweekly_weekday_error", {
						...biweeklyAnalyticsProps,
						error_message: (e as { message?: string })?.message || "unknown",
					});
				}
				//exception log handled in the hook
				onFinished(false, e);
			}
		}
	}, [
		handleDeleteSingle,
		onFinished,
		dateToDeleteFrom,
		selectedMode,
		nurseInShift,
		source,
		chosenDepartment.department_id,
		deleteRecurringShift,
		dontRenderReasonSelector,
		isSelfSchedulingShift,
		applyImmediately,
		isReasonValid,
		reasonShakeControls,
		track,
		trackError,
		shiftDay.loadWeeks,
		getNurseById,
		showActual,
		payrollChangesNeedConfirmation,
		didConfirmPayrollWarning,
		showErrorSnackbar,
		t,
	]);

	const isLoading = removeNurseLoading || deleteRecurringShiftLoading || isCallOffPending || shiftDay.isLoadingForAction;

	return {
		track,
		handleConfirm,
		deleteValid,
		selectedModeChangeHandler,
		selectedMode,
		allowRecurringModes,
		setDateToDeleteFrom,
		setDeleteUpdateDateSelectionValid,
		dontRenderReasonSelector,
		isSelfSchedulingShift,
		selectedReason,
		showReasonError,
		reasonShakeControls,
		handleReasonChange,
		removalMessage,
		setRemovalMessage,
		needsPayrollConfirmation,
		setDidConfirmPayrollWarning,
		isLoading,
		renderFindReplacementForShift,
		shouldFindReplacementForShift,
		setShouldFindReplacementForShift,
		isReplacementGateReady: isGateReady,
		isReplacementGateError: isGateError,
		// Null while the gate is unknown (loading or failed) — never a decision.
		replacementAdvice,
		isReplacementHintOpen: replacementAdvice !== null && isCheckboxHintOpen(replacementAdvice, shouldFindReplacementForShift),
		shouldWarnRemovingUnion,
		callInTime,
		setCallInTime,
		callInDate,
		setCallInDate,
	};
};
